import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { GlobalAuthModalProvider } from '@/components/auth/GlobalAuthModal';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { GamificationFeatureGate } from '@/components/gamification/gamification-feature-gate';
import { GamificationUpdateCoordinator } from '@/components/gamification/gamification-update-coordinator';
import { ProgressTrophiesModal } from '@/components/gamification/progress-trophies-modal';
import { OnboardingModal } from '@/components/onboarding';
import { GlobalNotificationPreferencesModal } from '@/components/profile/GlobalNotificationPreferencesModal';
import { CountryPromptModalProvider } from '@/components/shared/CountryPromptModalProvider';
import { RevoteConfirmModalProvider } from '@/components/shared/RevoteConfirmModalProvider';
import { XFollowModalProvider } from '@/components/shared/XFollowModalProvider';
import { TestErrorBoundary } from '@/components/TestErrorBoundary';
import { useMe } from '@/hooks/data/auth';
import { useMaintenanceStatus } from '@/hooks/data/config';
import { useMyProfile } from '@/hooks/data/profiles';
import { notify } from '@/services/notifications';
import DuelDetail from './pages/DuelDetail';
import Duels from './pages/Duels';
import Dashboard from './pages/Home';
import Maintenance from './pages/Maintenance';
import NotFound from './pages/NotFound';
import { QueryProvider } from './providers/QueryProvider';

// Eager: `/`, `/duels`, `/duel/:slug` are the traffic and LCP paths, plus the 404 and
// maintenance shells. Everything else is route-split — it was ~46 KB of `/test/*` alone.
const CaptainRating = lazy(() => import('./pages/CaptainRating'));
const EmailPreferences = lazy(() => import('./pages/EmailPreferences'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileRedirect = lazy(() => import('./pages/ProfileRedirect'));
const Rating = lazy(() => import('./pages/Rating'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Terms = lazy(() => import('./pages/Terms'));
const TestCards = lazy(() => import('./pages/TestCards'));
const TestProfileVotes = lazy(() => import('./pages/TestProfileVotes'));
const TestRevoteLimits = lazy(() => import('./pages/TestRevoteLimits'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));

const Captain = lazy(() => import('./pages/Captain'));
const CrowdRating = lazy(() => import('./pages/CrowdRating'));
const CrowdRatingHowItWorks = lazy(() => import('./pages/CrowdRatingHowItWorks'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const BrowseLeagues = lazy(() => import('./pages/BrowseLeagues'));
const Leagues = lazy(() => import('./pages/Leagues'));
const LeagueDetail = lazy(() => import('./pages/LeagueDetail'));
const LeagueJoin = lazy(() => import('./pages/LeagueJoin'));
const HowLeaguesWork = lazy(() => import('./pages/HowLeaguesWork'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogSegment = lazy(() => import('./pages/BlogSegment'));
const showDevDebugWidget = import.meta.env.VITE_DEV_DEBUG_WIDGET === 'true';
const LazyDebugWidget = showDevDebugWidget
  ? lazy(() => import('./components/dev/DebugWidget'))
  : null;

/** Stable height while lazy route chunks load — avoids blank→full CLS. */
const RouteChunkFallback = () => (
  <div className="min-h-screen bg-background" aria-busy="true" aria-live="polite" />
);

/** Слушает postMessage от OAuth popup при ошибке link_account — показывает toast на родительской странице */
function OAuthLinkErrorListener() {
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === 'oauth_link_error' && typeof e.data.message === 'string') {
        notify.error(e.data.message);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  return null;
}

/** После успешного refresh токена в apiRequest инвалидируем дуэли, чтобы подтянуть воуты с новым токеном */
function AuthTokenRefreshedListener() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['duels'] });
    };
    window.addEventListener('auth-token-refreshed', handler);
    return () => window.removeEventListener('auth-token-refreshed', handler);
  }, [queryClient]);
  return null;
}

// Главный компонент приложения с маршрутизацией
function AppRoutes() {
  // Тянем данные текущего пользователя на старте приложения
  useMe();
  // Preload full profile (incl. ui_flags) for Follow X modal trigger
  useMyProfile();

  // Проверяем статус maintenance с поллингом каждые 30 секунд
  const { data: maintenanceStatus } = useMaintenanceStatus();

  // Если включен режим обслуживания, показываем страницу Maintenance
  if (maintenanceStatus?.enabled) {
    return <Maintenance message={maintenanceStatus.message} />;
  }

  return (
    <div className="min-h-screen bg-background font-body antialiased">
      <AuthTokenRefreshedListener />
      <OAuthLinkErrorListener />
      <Suspense fallback={<RouteChunkFallback />}>
        <Routes>
          {/* Публичные маршруты */}
          <Route path="/" element={<Dashboard />} />
          <Route path="/duels" element={<Duels />} />
          <Route path="/duel/:slug" element={<DuelDetail />} />
          <Route path="/captain" element={<Captain />} />
          <Route path="/crowd-rating" element={<CrowdRating />} />
          <Route path="/crowd-rating/how-it-works" element={<CrowdRatingHowItWorks />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/leagues" element={<Leagues />} />
          <Route path="/leagues/browse" element={<BrowseLeagues />} />
          <Route path="/leagues/join/:code" element={<LeagueJoin />} />
          <Route path="/leagues/:id" element={<LeagueDetail />} />
          <Route path="/profile" element={<ProfileRedirect />} />
          <Route path="/profile/:username" element={<Profile />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/how-leagues-work" element={<HowLeaguesWork />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:segment" element={<BlogSegment />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/rating" element={<Rating />} />
          <Route path="/captain-rating" element={<CaptainRating />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/email-preferences" element={<EmailPreferences />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/test/cards" element={<TestCards />} />
          <Route path="/test/revote-limits" element={<TestRevoteLimits />} />
          <Route path="/test/profile-votes" element={<TestProfileVotes />} />
          <Route path="/test/error" element={<TestErrorBoundary />} />
          {/* 404 - Catch all неизвестных маршрутов */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <GlobalAuthModalProvider>
          <AppRoutes />
          <GlobalNotificationPreferencesModal />
          <OnboardingModal />
          <XFollowModalProvider />
          <CountryPromptModalProvider />
          <RevoteConfirmModalProvider />
          <GamificationFeatureGate>
            <GamificationUpdateCoordinator />
            <ProgressTrophiesModal />
          </GamificationFeatureGate>
          {LazyDebugWidget && (
            <Suspense fallback={null}>
              <LazyDebugWidget />
            </Suspense>
          )}
        </GlobalAuthModalProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}

export default App;
